import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wallet, Plus, Trash2, Loader2, Save, X, TrendingDown,
  Package, Users, Coins, Info, Printer, Share2, Receipt,
  Search, Calendar, FileText, BookOpen, ArrowUpCircle, ArrowDownCircle,
} from "lucide-react";
import { printHTML } from "../../utils/printHTML";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB"); } catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ar-IQ", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

// ─── Amount → Arabic words (simple version, up to millions) ─────
const toArabicWords = (n) => {
  const num = Math.floor(Number(n) || 0);
  if (num === 0) return "صفر دينار";
  if (num < 1000) return `${num} دينار`;
  if (num < 1000000) return `${fmt(Math.floor(num / 1000))} ألف و ${num % 1000} دينار`;
  const millions = Math.floor(num / 1000000);
  const rem = num % 1000000;
  const thousands = Math.floor(rem / 1000);
  const ones = rem % 1000;
  let s = `${millions} مليون`;
  if (thousands) s += ` و ${thousands} ألف`;
  if (ones) s += ` و ${ones}`;
  return s + " دينار";
};

// ─── Investor Voucher Print HTML ────────────────────────────────
const buildVoucherHTML = (v) => `<!doctype html>
<html dir="rtl" lang="ar"><head><meta charset="UTF-8"><title>سند قبض #${v.voucher_no}</title>
<style>
  @page { size: A5; margin: 5mm; }
  html, body { margin:0; padding:0; background:#fff; color:#000; font-family:'Tajawal','Cairo','Tahoma',sans-serif; }
  body { padding: 10mm 8mm; }
  .frame { border: 3px solid #059669; border-radius: 12px; padding: 16px; max-width: 540px; margin: 0 auto; }
  .head { text-align:center; border-bottom: 2px solid #059669; padding-bottom: 10px; margin-bottom: 12px; }
  .head h1 { margin: 0; color:#059669; font-size: 26pt; font-weight: 900; }
  .head p { margin: 2px 0; font-size: 10pt; }
  .badge { background:#059669; color:#fff; padding: 6px 12px; border-radius: 8px; text-align:center; font-size:14pt; font-weight:900; margin-bottom: 10px; }
  table { width:100%; border-collapse:collapse; font-size:11pt; }
  td { border:1px solid #000; padding: 6px; }
  .lbl { background:#f0fdf4; font-weight:900; width:32%; }
  .val { font-weight:700; }
  .big { font-size:16pt; color:#059669; font-weight:900; }
  .words { padding: 6px; border:1px dashed #059669; margin-top: 8px; font-weight:700; text-align:center; background:#f0fdf4; }
  .sig { display:flex; justify-content:space-between; margin-top: 30px; font-size:10pt; }
  .sig div { text-align:center; width:45%; }
  .sig div span { border-top:1px solid #000; display:block; padding-top: 5px; }
  .footer { margin-top: 20px; text-align:center; font-size:9pt; color:#666; border-top: 1px solid #eee; padding-top: 6px; }
</style></head><body>
<div class="frame">
  <div class="head">
    <h1>جُملَتي</h1>
    <p><b>إدارة مكتب الأمين</b></p>
    <p>العراق — كربلاء المقدسة — 07717637525 — 07749330887</p>
  </div>
  <div class="badge">سند قبض للصندوق</div>
  <table>
    <tr><td class="lbl">رقم السند</td><td class="val big">#${v.voucher_no}</td></tr>
    <tr><td class="lbl">التاريخ</td><td class="val">${fmtDate(v.voucher_date || v.created_at)}</td></tr>
    <tr><td class="lbl">استلمنا من السيد/ة</td><td class="val big">${v.investor_name || "—"}</td></tr>
    <tr><td class="lbl">المصدر</td><td class="val">${v.source || "استثمار"}</td></tr>
    <tr><td class="lbl">المبلغ</td><td class="val big" dir="ltr">${fmt(v.amount)} د.ع</td></tr>
    ${v.note ? `<tr><td class="lbl">ملاحظات</td><td>${v.note}</td></tr>` : ""}
  </table>
  <div class="words">وقدره: ${toArabicWords(v.amount)}</div>
  <div class="sig">
    <div><span>توقيع المُسلِّم</span></div>
    <div><span>توقيع المُستلِم</span></div>
  </div>
  <div class="footer">جُملَتي — Jumlaty · jumlaty.online</div>
</div>
</body></html>`;


// ─── Voucher Modal (create + view + list) ───────────────────────
const VouchersModal = ({ onClose, onCreated }) => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState({ from: "", to: "", no: "" });

  const [form, setForm] = useState({ investor_name: "", amount: "", note: "", source: "استثمار" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = {};
    if (filters.from) params.date_from = filters.from;
    if (filters.to) params.date_to = filters.to;
    if (filters.no) params.voucher_no = filters.no;
    try {
      const { data } = await axios.get(`${API}/admin/admin-accounts/vouchers`, { params });
      setVouchers(data || []);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const submit = async () => {
    const amt = parseFloat(String(form.amount).replace(/,/g, ""));
    if (!form.investor_name.trim()) { toast.error("اسم المستثمر مطلوب"); return; }
    if (isNaN(amt) || amt <= 0) { toast.error("مبلغ غير صحيح"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/admin-accounts/vouchers`, {
        investor_name: form.investor_name.trim(),
        amount: amt,
        note: form.note.trim(),
        source: form.source.trim() || "استثمار",
      });
      toast.success(`تم تسجيل سند القبض #${data.voucher_no}`);
      setForm({ investor_name: "", amount: "", note: "", source: "استثمار" });
      setShowForm(false);
      load();
      onCreated?.();
      // Auto-print the new voucher
      setTimeout(() => printHTML(buildVoucherHTML(data), { title: `سند قبض #${data.voucher_no}` }), 300);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`حذف سند #${v.voucher_no}؟ راح ينقص المبلغ من صندوق النقد.`)) return;
    try {
      await axios.delete(`${API}/admin/admin-accounts/vouchers/${v.id}`);
      toast.success("تم الحذف");
      load();
      onCreated?.();
    } catch { toast.error("فشل الحذف"); }
  };

  const printOne = (v) => printHTML(buildVoucherHTML(v), { title: `سند قبض #${v.voucher_no}` });

  const shareOne = (v) => {
    const lines = [
      "*📄 سند قبض — جملتي*",
      `رقم السند: *#${v.voucher_no}*`,
      `التاريخ: ${fmtDate(v.voucher_date || v.created_at)}`,
      `المستلم منه: *${v.investor_name}*`,
      `المصدر: ${v.source || "استثمار"}`,
      `المبلغ: *${fmt(v.amount)} د.ع*`,
      v.note ? `ملاحظات: ${v.note}` : "",
      "",
      "شكراً لثقتكم — جُملَتي",
    ].filter(Boolean).join("\n");
    window.open(`https://wa.me/?text=${encodeURIComponent(lines)}`, "_blank");
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" dir="rtl" data-testid="vouchers-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-3xl max-h-[92vh] shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-l from-emerald-700 to-emerald-500 text-white p-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Receipt className="w-6 h-6" />
            <div>
              <h3 className="font-bold text-lg">سندات القبض للصندوق</h3>
              <p className="text-[11px] opacity-90">تسجيل استثمارات / إيداعات في صندوق النقد</p>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="close-vouchers-modal" className="bg-white/20 hover:bg-white/30 rounded-full p-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filter + Add */}
        <div className="p-3 bg-emerald-50 border-b border-emerald-200 flex flex-wrap gap-2 items-end shrink-0">
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] font-bold text-emerald-800 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> من
            </label>
            <input type="date" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              className="px-2 py-1.5 rounded-lg border border-emerald-300 text-xs" data-testid="filter-from"/>
          </div>
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] font-bold text-emerald-800 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> إلى
            </label>
            <input type="date" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              className="px-2 py-1.5 rounded-lg border border-emerald-300 text-xs" data-testid="filter-to"/>
          </div>
          <div className="flex flex-col gap-0.5 flex-1 min-w-[100px]">
            <label className="text-[10px] font-bold text-emerald-800 flex items-center gap-1">
              <Search className="w-3 h-3" /> رقم الوصل
            </label>
            <input type="number" value={filters.no} onChange={(e) => setFilters((f) => ({ ...f, no: e.target.value }))}
              placeholder="مثلاً 5" className="px-2 py-1.5 rounded-lg border border-emerald-300 text-xs" data-testid="filter-no"/>
          </div>
          <button type="button" onClick={load} className="px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-800" data-testid="apply-filters">
            بحث
          </button>
          <button type="button" onClick={() => setShowForm(true)} className="px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-bold hover:bg-orange-600 flex items-center gap-1" data-testid="new-voucher-btn">
            <Plus className="w-3.5 h-3.5" /> سند جديد
          </button>
        </div>

        {/* Add form */}
        {showForm && (
          <div className="p-3 border-b bg-white shrink-0" data-testid="voucher-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input type="text" placeholder="اسم المستثمر *" value={form.investor_name}
                onChange={(e) => setForm({ ...form, investor_name: e.target.value })}
                className="px-3 py-2 rounded-lg border-2 border-emerald-300 focus:border-emerald-600 outline-none" data-testid="voucher-name"/>
              <input type="text" inputMode="numeric" placeholder="المبلغ *" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="px-3 py-2 rounded-lg border-2 border-emerald-300 focus:border-emerald-600 outline-none font-bold" data-testid="voucher-amount"/>
              <input type="text" placeholder="المصدر (استثمار/إيداع...)" value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                className="px-3 py-2 rounded-lg border-2 border-emerald-300 focus:border-emerald-600 outline-none" data-testid="voucher-source"/>
              <input type="text" placeholder="ملاحظة (اختياري)" value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="px-3 py-2 rounded-lg border-2 border-emerald-300 focus:border-emerald-600 outline-none" data-testid="voucher-note"/>
            </div>
            <div className="flex gap-2 mt-2">
              <button type="button" onClick={submit} disabled={saving}
                className="flex-1 py-2 rounded-lg bg-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-1" data-testid="submit-voucher">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                حفظ وطباعة
              </button>
              <button type="button" onClick={() => setShowForm(false)}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 font-bold">إلغاء</button>
            </div>
          </div>
        )}

        {/* List */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3">
          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin inline text-emerald-600" /></div>
          ) : vouchers.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm" data-testid="no-vouchers">لا توجد سندات مطابقة</div>
          ) : (
            <div className="space-y-2">
              {vouchers.map((v) => (
                <div key={v.id} className="border-2 border-emerald-200 rounded-xl p-3 bg-emerald-50/40 hover:bg-emerald-50" data-testid={`voucher-row-${v.voucher_no}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-black text-emerald-800 text-lg">#{v.voucher_no}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-gray-900 truncate">{v.investor_name}</div>
                      <div className="text-[10px] text-gray-500">
                        {fmtDateTime(v.created_at)} · {v.source || "استثمار"}
                        {v.note ? ` · ${v.note}` : ""}
                      </div>
                    </div>
                    <div className="text-xl font-black text-emerald-700" dir="ltr">+{fmt(v.amount)}</div>
                    <div className="flex gap-1">
                      <button onClick={() => printOne(v)} className="p-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700" title="طباعة" data-testid={`print-voucher-${v.voucher_no}`}>
                        <Printer className="w-4 h-4" />
                      </button>
                      <button onClick={() => shareOne(v)} className="p-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700" title="واتساب" data-testid={`share-voucher-${v.voucher_no}`}>
                        <Share2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(v)} className="p-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600" title="حذف" data-testid={`delete-voucher-${v.voucher_no}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ─── Cash Box Ledger Modal (حسابات الصندوق) ─────────────────────
const LEDGER_TYPE_LABELS = {
  base: "رصيد ابتدائي",
  receipt: "قبض من زبون",
  investor: "قبض استثمار",
  supplier_payment: "دفع لمُجهّز",
  purchase_cash: "شراء نقدي",
  expense: "مصروف",
};

const CashBoxLedgerModal = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ from: "", to: "", type: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.from) params.date_from = filters.from;
      if (filters.to) params.date_to = filters.to;
      const { data: d } = await axios.get(`${API}/admin/admin-accounts/ledger`, { params });
      setData(d);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const visibleRows = (data?.rows || []).filter((r) => !filters.type || r.type === filters.type);

  const printLedger = () => {
    if (!data) return;
    const rowsHTML = visibleRows.map((r, i) => `
      <tr>
        <td>${visibleRows.length - i}</td>
        <td>${fmtDateTime(r.date)}</td>
        <td><span class="badge ${r.direction}">${LEDGER_TYPE_LABELS[r.type] || r.type}</span></td>
        <td>${r.label}</td>
        <td>${r.reference || "—"}</td>
        <td class="${r.direction === 'in' ? 'in' : 'out'}" dir="ltr">${r.direction === 'in' ? '+' : '−'}${fmt(r.amount)}</td>
        <td dir="ltr" class="run">${fmt(r.running_balance)}</td>
      </tr>`).join("");
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="UTF-8"><title>حسابات الصندوق</title>
    <style>
      @page{size:A4 landscape;margin:8mm}body{font-family:'Tajawal','Tahoma',sans-serif;padding:8px;color:#000;font-size:10pt}
      h1{background:linear-gradient(135deg,#059669,#10b981);color:#fff;text-align:center;font-size:16pt;margin:0 0 6px;padding:8px;border-radius:8px}
      .sub{text-align:center;color:#666;margin-bottom:8px;font-size:9pt}
      .stats{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin-bottom:8px}
      .stat{background:#f0fdf4;border-right:3px solid #059669;padding:6px;border-radius:6px;text-align:center}
      .stat b{display:block;font-size:14pt;color:#065f46}
      .stat span{font-size:8pt;color:#059669}
      table{width:100%;border-collapse:collapse;font-size:9pt}
      th{background:#065f46;color:#fff;padding:5px;text-align:right}
      td{border:1px solid #ddd;padding:4px}
      .badge{padding:2px 6px;border-radius:4px;font-size:8pt;font-weight:900}
      .badge.in{background:#dcfce7;color:#065f46}
      .badge.out{background:#fee2e2;color:#991b1b}
      .in{color:#065f46;font-weight:900}
      .out{color:#991b1b;font-weight:900}
      .run{font-weight:900;color:#1e3a5f;background:#f8fafc}
    </style></head><body>
    <h1>حسابات الصندوق — سجل حركات النقد</h1>
    <p class="sub">${new Date().toLocaleDateString("ar-IQ")} · ${visibleRows.length} حركة</p>
    <div class="stats">
      <div class="stat"><b dir="ltr">+${fmt(data.totals_in)}</b><span>إجمالي الداخل</span></div>
      <div class="stat"><b dir="ltr">−${fmt(data.totals_out)}</b><span>إجمالي الخارج</span></div>
      <div class="stat"><b dir="ltr">${fmt(data.closing_balance)}</b><span>الرصيد الحالي</span></div>
    </div>
    <table>
      <thead><tr><th>#</th><th>التاريخ</th><th>النوع</th><th>الوصف</th><th>المرجع</th><th>المبلغ</th><th>الرصيد</th></tr></thead>
      <tbody>${rowsHTML}</tbody>
    </table>
    </body></html>`;
    printHTML(html, { title: "حسابات الصندوق" });
  };

  return (
    <div className="fixed inset-0 z-[115] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-3" dir="rtl" data-testid="ledger-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-6xl max-h-[92vh] shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-emerald-800 to-emerald-500 text-white p-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <BookOpen className="w-6 h-6" />
            <div>
              <h3 className="font-bold text-lg">حسابات الصندوق</h3>
              <p className="text-[11px] opacity-90">كل حركات صندوق النقد (داخل / خارج) مرتبة بالتاريخ</p>
            </div>
          </div>
          <div className="flex gap-1.5">
            <button type="button" onClick={printLedger} className="bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-1" data-testid="print-ledger">
              <Printer className="w-4 h-4" /> طباعة
            </button>
            <button type="button" onClick={onClose} className="bg-white/20 hover:bg-white/30 rounded-full p-2" data-testid="close-ledger">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Stats + filters */}
        <div className="p-3 bg-emerald-50/50 border-b border-emerald-200 shrink-0 space-y-2">
          {!loading && data && (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-white rounded-lg p-2 border-r-4 border-green-500">
                <div className="text-[10px] text-gray-500">إجمالي الداخل</div>
                <div className="text-lg font-black text-green-700" dir="ltr">+{fmt(data.totals_in)}</div>
              </div>
              <div className="bg-white rounded-lg p-2 border-r-4 border-red-500">
                <div className="text-[10px] text-gray-500">إجمالي الخارج</div>
                <div className="text-lg font-black text-red-700" dir="ltr">−{fmt(data.totals_out)}</div>
              </div>
              <div className="bg-white rounded-lg p-2 border-r-4 border-blue-500">
                <div className="text-[10px] text-gray-500">الرصيد الحالي</div>
                <div className="text-lg font-black text-blue-700" dir="ltr">{fmt(data.closing_balance)}</div>
              </div>
            </div>
          )}
          <div className="flex gap-2 flex-wrap items-end">
            <div className="flex flex-col gap-0.5">
              <label className="text-[10px] font-bold text-emerald-800">من</label>
              <input type="date" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                className="px-2 py-1.5 rounded-lg border border-emerald-300 text-xs" data-testid="ledger-from"/>
            </div>
            <div className="flex flex-col gap-0.5">
              <label className="text-[10px] font-bold text-emerald-800">إلى</label>
              <input type="date" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                className="px-2 py-1.5 rounded-lg border border-emerald-300 text-xs" data-testid="ledger-to"/>
            </div>
            <div className="flex flex-col gap-0.5 flex-1 min-w-[120px]">
              <label className="text-[10px] font-bold text-emerald-800">النوع</label>
              <select value={filters.type} onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
                className="px-2 py-1.5 rounded-lg border border-emerald-300 text-xs bg-white" data-testid="ledger-type">
                <option value="">كل الحركات</option>
                <option value="receipt">قبض من زبون</option>
                <option value="investor">قبض استثمار</option>
                <option value="supplier_payment">دفع لمُجهّز</option>
                <option value="purchase_cash">شراء نقدي</option>
                <option value="expense">مصروف</option>
                <option value="base">رصيد ابتدائي</option>
              </select>
            </div>
            <button type="button" onClick={load} className="px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-800" data-testid="apply-ledger-filter">
              بحث
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 overflow-auto p-2">
          {loading || !data ? (
            <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin inline text-emerald-600" /></div>
          ) : visibleRows.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm" data-testid="no-ledger-rows">لا توجد حركات مطابقة</div>
          ) : (
            <table className="w-full text-xs" data-testid="ledger-table">
              <thead className="sticky top-0 bg-emerald-800 text-white z-10">
                <tr>
                  <th className="p-2 text-right">#</th>
                  <th className="p-2 text-right">التاريخ</th>
                  <th className="p-2 text-right">النوع</th>
                  <th className="p-2 text-right">الوصف</th>
                  <th className="p-2 text-right">المرجع</th>
                  <th className="p-2 text-left">المبلغ</th>
                  <th className="p-2 text-left">الرصيد</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r, i) => (
                  <tr key={r.type + "-" + r.id + "-" + i}
                    className={`border-b hover:bg-emerald-50 ${r.direction === 'in' ? 'bg-green-50/30' : 'bg-red-50/20'}`}
                    data-testid={`ledger-row-${r.id}`}>
                    <td className="p-2 font-bold text-gray-600">{visibleRows.length - i}</td>
                    <td className="p-2 whitespace-nowrap">{fmtDateTime(r.date)}</td>
                    <td className="p-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${
                        r.direction === 'in' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {r.direction === 'in' ? <ArrowUpCircle className="w-3 h-3" /> : <ArrowDownCircle className="w-3 h-3" />}
                        {LEDGER_TYPE_LABELS[r.type] || r.type}
                      </span>
                    </td>
                    <td className="p-2 font-bold text-gray-900">
                      {r.label}
                      {r.note && <div className="text-[10px] text-gray-500 font-normal">{r.note}</div>}
                    </td>
                    <td className="p-2 font-mono text-gray-700">{r.reference || "—"}</td>
                    <td className={`p-2 font-black text-left ${r.direction === 'in' ? 'text-green-700' : 'text-red-700'}`} dir="ltr">
                      {r.direction === 'in' ? '+' : '−'}{fmt(r.amount)}
                    </td>
                    <td className="p-2 font-black text-blue-700 text-left bg-blue-50" dir="ltr">{fmt(r.running_balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};


// ─── Main Admin Accounts Tab ────────────────────────────────────
const AdminAccountsTab = () => {
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCashEdit, setShowCashEdit] = useState(false);
  const [cashInput, setCashInput] = useState("");
  const [savingCash, setSavingCash] = useState(false);

  const [expenseAmount, setExpenseAmount] = useState("");
  const [expenseNote, setExpenseNote] = useState("");
  const [expenseCat, setExpenseCat] = useState("أخرى");
  const [savingExpense, setSavingExpense] = useState(false);

  const [showVouchers, setShowVouchers] = useState(false);
  const [showLedger, setShowLedger] = useState(false);

  const load = async () => {
    try {
      const [s, e] = await Promise.all([
        axios.get(`${API}/admin/admin-accounts/summary`),
        axios.get(`${API}/admin/admin-accounts/expenses`),
      ]);
      setSummary(s.data);
      setExpenses(e.data || []);
    } catch (err) { toast.error("فشل تحميل الحسابات"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const saveCashBox = async () => {
    const amount = parseFloat(String(cashInput).replace(/,/g, ""));
    if (isNaN(amount) || amount < 0) { toast.error("أدخل مبلغاً صحيحاً"); return; }
    if (!window.confirm(`تأكيد ضبط رصيد صندوق النقد على ${fmt(amount)} د.ع؟\n⚠️ هذي القيمة راح تصير نقطة البداية.`)) return;
    setSavingCash(true);
    try {
      await axios.post(`${API}/admin/admin-accounts/cash-box`, { amount });
      toast.success("تم ضبط رصيد صندوق النقد");
      setShowCashEdit(false); setCashInput(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "فشل الحفظ"); }
    finally { setSavingCash(false); }
  };

  const addExpense = async () => {
    const amount = parseFloat(String(expenseAmount).replace(/,/g, ""));
    if (isNaN(amount) || amount <= 0) { toast.error("أدخل مبلغاً صحيحاً"); return; }
    setSavingExpense(true);
    try {
      // Post to the SAME endpoint the ordinary "المصاريف" tile uses so
      // both entry points feed one shared list — never a duplicate ledger.
      await axios.post(`${API}/admin/expenses`, {
        amount, category: expenseCat || "أخرى", note: expenseNote.trim(),
      });
      toast.success("تم تسجيل المصروف");
      setExpenseAmount(""); setExpenseNote(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "فشل الحفظ"); }
    finally { setSavingExpense(false); }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm("حذف هذا المصروف؟ سيعود المبلغ إلى صندوق النقد.")) return;
    try {
      await axios.delete(`${API}/admin/expenses/${id}`);
      toast.success("تم الحذف"); load();
    } catch { toast.error("فشل الحذف"); }
  };

  // ─── Whole-tab share / print ──────────────────────────────────
  const buildSummaryHTML = () => `<!doctype html>
<html dir="rtl" lang="ar"><head><meta charset="UTF-8"><title>حسابات الإدارة</title>
<style>
  @page{size:A4;margin:10mm}body{font-family:'Tajawal','Tahoma',sans-serif;padding:12px;color:#000}
  h1{background:linear-gradient(135deg,#059669,#10b981);color:#fff;text-align:center;font-size:20pt;margin:0 0 8px;padding:12px;border-radius:10px}
  .sub{text-align:center;color:#666;font-size:11pt;margin-bottom:14px}
  .grand{background:#f0fdf4;border:3px solid #059669;border-radius:12px;padding:12px;text-align:center;margin-bottom:12px}
  .grand .lbl{font-size:12pt;color:#059669;font-weight:bold}
  .grand .val{font-size:28pt;font-weight:900;color:#065f46}
  table{width:100%;border-collapse:collapse;font-size:11pt;margin-bottom:12px}
  td,th{border:1px solid #ddd;padding:6px 8px;text-align:right}
  th{background:#f0fdf4;color:#059669}
  .amt{font-weight:900;color:#065f46;text-align:left}
  .expense-total{background:#fef2f2;color:#991b1b}
  .footer{margin-top:20px;text-align:center;font-size:9pt;color:#999}
</style></head><body>
<h1>حسابات الإدارة — جملتي</h1>
<p class="sub">تاريخ التقرير: ${new Date().toLocaleDateString("ar-IQ")}</p>
<div class="grand">
  <div class="lbl">الصافي — المبلغ الكلي (السيولة الحالية)</div>
  <div class="val" dir="ltr">${fmt(summary?.grand_total || 0)} د.ع</div>
</div>
<table>
  <tr><th>البند</th><th style="text-align:left">القيمة (د.ع)</th></tr>
  <tr><td>الديون اللي نطلبها (من الزبائن)</td><td class="amt" dir="ltr">${fmt(summary?.total_debts || 0)}</td></tr>
  <tr><td>البضاعة الموجودة في المخزن (بسعر الكلفة)</td><td class="amt" dir="ltr">${fmt(summary?.inventory_value || 0)}</td></tr>
  <tr><td>نقد الصندوق</td><td class="amt" dir="ltr">${fmt(summary?.cash_box || 0)}</td></tr>
  <tr><td class="expense-total">المصاريف (تُخصم من الصافي)</td><td class="amt expense-total" dir="ltr">${fmt(summary?.total_expenses || 0)}</td></tr>
</table>
<h3 style="color:#059669;font-size:13pt;margin:12px 0 6px">تفاصيل صندوق النقد:</h3>
<table>
  <tr><td>الأساس المُدخَل</td><td class="amt" dir="ltr">${fmt(summary?.cash_box_base || 0)}</td></tr>
  <tr><td>+ سندات قبض من الزبائن (بعد الأساس)</td><td class="amt" dir="ltr" style="color:#059669">+${fmt(summary?.cash_in_since_init || 0)}</td></tr>
  <tr><td>+ سندات قبض للاستثمار</td><td class="amt" dir="ltr" style="color:#059669">+${fmt(summary?.investor_total || 0)}</td></tr>
  <tr><td>− دفع للمجهزين (بعد الأساس)</td><td class="amt" dir="ltr" style="color:#dc2626">-${fmt(summary?.cash_out_since_init || 0)}</td></tr>
  <tr><td>− المصاريف</td><td class="amt" dir="ltr" style="color:#dc2626">-${fmt(summary?.total_expenses || 0)}</td></tr>
</table>
<div class="footer">جُملَتي — Jumlaty · jumlaty.online</div>
</body></html>`;

  const printSummary = () => printHTML(buildSummaryHTML(), { title: "حسابات الإدارة" });

  const shareSummary = () => {
    const lines = [
      "*📊 حسابات الإدارة — جملتي*",
      `التاريخ: ${new Date().toLocaleDateString("ar-IQ")}`,
      "─".repeat(24),
      `💰 *الصافي: ${fmt(summary?.grand_total || 0)} د.ع*`,
      "",
      `👥 الديون اللي نطلبها: ${fmt(summary?.total_debts || 0)}`,
      `📦 البضاعة الموجودة: ${fmt(summary?.inventory_value || 0)}`,
      `💵 نقد الصندوق: ${fmt(summary?.cash_box || 0)}`,
      `📉 المصاريف: ${fmt(summary?.total_expenses || 0)}`,
    ].join("\n");
    window.open(`https://wa.me/?text=${encodeURIComponent(lines)}`, "_blank");
  };

  if (loading || !summary) {
    return <div className="text-center py-16" data-testid="admin-accounts-loading"><Loader2 className="w-8 h-8 animate-spin inline text-green-600" /></div>;
  }

  const initDate = summary.cash_box_initialized_at ? new Date(summary.cash_box_initialized_at).toLocaleDateString("ar-IQ") : null;

  return (
    <div className="space-y-4" data-testid="admin-accounts-tab" dir="rtl">
      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 justify-end">
        <button type="button" onClick={printSummary} className="px-3 py-2 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 flex items-center gap-1 shadow" data-testid="print-summary-btn">
          <Printer className="w-4 h-4" /> طباعة
        </button>
        <button type="button" onClick={shareSummary} className="px-3 py-2 rounded-xl bg-green-600 text-white text-sm font-bold hover:bg-green-700 flex items-center gap-1 shadow" data-testid="share-summary-btn">
          <Share2 className="w-4 h-4" /> واتساب
        </button>
      </div>

      {/* GRAND TOTAL */}
      <div className="rounded-3xl p-6 text-white shadow-2xl relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 50%, #059669 100%)" }} data-testid="grand-total-card">
        <div className="absolute -top-8 -left-8 w-40 h-40 rounded-full bg-white/10" />
        <div className="absolute -bottom-10 -right-10 w-52 h-52 rounded-full bg-white/5" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 opacity-90 text-sm font-bold mb-2">
            <Coins className="w-5 h-5" />
            <span>الصافي — المبلغ الكلي (السيولة الحالية)</span>
          </div>
          <div className="text-5xl md:text-6xl font-black" dir="ltr" data-testid="grand-total-value">{fmt(summary.grand_total)}</div>
          <div className="text-xs opacity-80 mt-1">دينار عراقي</div>
          <div className="mt-4 text-[11px] opacity-90 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>الديون + البضاعة + صندوق النقد − المصاريف</span>
          </div>
        </div>
      </div>

      {/* 4 cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl p-4 border-2 border-blue-200 shadow-md" data-testid="debts-card">
          <div className="flex items-center gap-2 text-blue-700 text-sm font-bold mb-1">
            <Users className="w-4 h-4" /><span>الديون اللي نطلبها</span>
            <span className="text-[10px] font-normal opacity-70 mr-auto">(تلقائي)</span>
          </div>
          <div className="text-2xl md:text-3xl font-black text-blue-900" dir="ltr" data-testid="debts-value">{fmt(summary.total_debts)}</div>
          <div className="text-[10px] text-blue-600 mt-1">من أرصدة حسابات الزبائن</div>
        </div>

        <div className="bg-white rounded-2xl p-4 border-2 border-orange-200 shadow-md" data-testid="inventory-card">
          <div className="flex items-center gap-2 text-orange-700 text-sm font-bold mb-1">
            <Package className="w-4 h-4" /><span>البضاعة الموجودة</span>
            <span className="text-[10px] font-normal opacity-70 mr-auto">(تلقائي)</span>
          </div>
          <div className="text-2xl md:text-3xl font-black text-orange-900" dir="ltr" data-testid="inventory-value">{fmt(summary.inventory_value)}</div>
          <div className="text-[10px] text-orange-600 mt-1">قيمة المخزن بسعر الكلفة</div>
        </div>

        <div className="bg-white rounded-2xl p-4 border-2 border-emerald-300 shadow-md md:col-span-2" data-testid="cashbox-card">
          <div className="flex items-center gap-2 text-emerald-700 text-sm font-bold mb-1 flex-wrap">
            <Wallet className="w-4 h-4" /><span>نقد الصندوق</span>
            <div className="mr-auto flex gap-1.5 flex-wrap">
              <button type="button" onClick={() => setShowLedger(true)}
                className="text-[11px] bg-blue-600 text-white px-2 py-1 rounded-lg hover:bg-blue-700 font-bold flex items-center gap-1" data-testid="open-ledger-btn">
                <BookOpen className="w-3 h-3" /> حسابات الصندوق
              </button>
              <button type="button" onClick={() => setShowVouchers(true)}
                className="text-[11px] bg-orange-500 text-white px-2 py-1 rounded-lg hover:bg-orange-600 font-bold flex items-center gap-1" data-testid="open-vouchers-btn">
                <Receipt className="w-3 h-3" /> سندات القبض
              </button>
              <button type="button" onClick={() => { setShowCashEdit(true); setCashInput(String(summary.cash_box_base || "")); }}
                className="text-[11px] bg-emerald-600 text-white px-2 py-1 rounded-lg hover:bg-emerald-700 font-bold" data-testid="edit-cashbox-btn">
                تعديل الرصيد
              </button>
            </div>
          </div>
          <div className="text-2xl md:text-3xl font-black text-emerald-900" dir="ltr" data-testid="cashbox-value">{fmt(summary.cash_box)}</div>
          <div className="text-[11px] text-emerald-700 mt-2 grid grid-cols-2 md:grid-cols-5 gap-2">
            <div><span className="opacity-70">أساس:</span> <span dir="ltr" className="font-bold">{fmt(summary.cash_box_base)}</span></div>
            <div><span className="opacity-70">+ قبض زبائن:</span> <span dir="ltr" className="font-bold text-green-700">{fmt(summary.cash_in_since_init)}</span></div>
            <div><span className="opacity-70">+ استثمارات:</span> <span dir="ltr" className="font-bold text-green-700">{fmt(summary.investor_total)}</span></div>
            <div><span className="opacity-70">− دفع مجهزين:</span> <span dir="ltr" className="font-bold text-red-700">{fmt(summary.cash_out_since_init)}</span></div>
            <div><span className="opacity-70">− مصاريف:</span> <span dir="ltr" className="font-bold text-red-700">{fmt(summary.total_expenses)}</span></div>
          </div>
          {initDate ? (
            <div className="text-[10px] text-emerald-600 mt-2 opacity-80">تم ضبط الأساس بتاريخ {initDate} — كل قبض / دفع بعد هذا التاريخ يُحسب</div>
          ) : (
            <div className="text-[11px] text-red-600 mt-2 font-bold">⚠️ لم يتم ضبط رصيد أساسي بعد — اضغط "تعديل الرصيد" لبدء التتبع</div>
          )}
        </div>
      </div>

      {/* Expenses */}
      <div className="bg-white rounded-2xl p-4 border-2 border-red-200 shadow-md" data-testid="expenses-section">
        <div className="flex items-center gap-2 text-red-700 text-sm font-bold mb-3">
          <TrendingDown className="w-4 h-4" /><span>المصاريف</span>
          <span className="mr-auto text-[11px] font-normal opacity-70">
            المجموع: <span dir="ltr" className="font-black">{fmt(summary.total_expenses)}</span> د.ع
          </span>
        </div>

        <div className="bg-red-50 rounded-xl p-3 mb-3 space-y-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <input type="text" inputMode="numeric" placeholder="المبلغ *" value={expenseAmount}
              onChange={(e) => setExpenseAmount(e.target.value)}
              className="px-3 py-2 rounded-lg border-2 border-red-200 focus:border-red-500 outline-none text-lg font-bold col-span-2 md:col-span-1"
              data-testid="expense-amount-input"/>
            <select value={expenseCat} onChange={(e) => setExpenseCat(e.target.value)}
              className="px-3 py-2 rounded-lg border-2 border-red-200 focus:border-red-500 outline-none bg-white col-span-2 md:col-span-1"
              data-testid="expense-cat-input">
              <option>محروقات</option><option>كهرباء</option><option>ماء</option>
              <option>إيجار</option><option>صيانة</option><option>رواتب</option>
              <option>نقل</option><option>مواد نظافة</option><option>ضيافة</option>
              <option>أخرى</option>
            </select>
            <input type="text" placeholder="ملاحظة (اختياري)" value={expenseNote}
              onChange={(e) => setExpenseNote(e.target.value)}
              className="px-3 py-2 rounded-lg border-2 border-red-200 focus:border-red-500 outline-none col-span-2 md:col-span-1"
              data-testid="expense-note-input"/>
            <button type="button" onClick={addExpense} disabled={savingExpense}
              className="px-4 py-2 rounded-lg bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1"
              data-testid="add-expense-btn">
              {savingExpense ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              إضافة
            </button>
          </div>
          <div className="text-[10px] text-red-700 opacity-80 flex items-start gap-1">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>هاي المصاريف تظهر أيضاً بتاب "المصاريف" الرئيسي وتنقص من الأرباح التراكمية للشهر الحالي.</span>
          </div>
        </div>

        {expenses.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm" data-testid="no-expenses">لا يوجد مصاريف مسجلة</div>
        ) : (
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
            {expenses.map((e) => (
              <div key={e.id} className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100" data-testid={`expense-row-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-900 truncate">
                    {e.category ? <span className="text-red-700 ml-1">[{e.category}]</span> : null}
                    {e.note || "بدون ملاحظة"}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    {fmtDateTime(e.created_at)}
                    {e.created_by_phone ? ` · ${e.created_by_phone}` : ""}
                  </div>
                </div>
                <div className="text-lg font-black text-red-700" dir="ltr">−{fmt(e.amount)}</div>
                <button type="button" onClick={() => deleteExpense(e.id)}
                  className="p-1.5 rounded-lg hover:bg-red-100 text-red-600" data-testid={`delete-expense-${e.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cashbox edit modal */}
      {showCashEdit && (
        <div className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-4" data-testid="cashbox-edit-modal">
          <div className="bg-white rounded-2xl p-5 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-emerald-900 flex items-center gap-2">
                <Wallet className="w-5 h-5" />ضبط رصيد صندوق النقد
              </h3>
              <button type="button" onClick={() => setShowCashEdit(false)} className="p-1 rounded hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                <div className="font-bold mb-1">⚠️ تنبيه</div>
                <div>هالمبلغ راح يصير نقطة البداية. كل سند قبض من الزبائن + سندات الاستثمار + سندات دفع للمجهزين + المصاريف بعد الحين راح ينحسب على هذا الرصيد.</div>
              </div>
              <label className="block">
                <span className="text-sm font-bold text-gray-700 mb-1 block">المبلغ الحالي بالصندوق</span>
                <input type="text" inputMode="numeric" autoFocus value={cashInput}
                  onChange={(e) => setCashInput(e.target.value)} placeholder="مثلاً: 50000000"
                  className="w-full px-3 py-3 rounded-lg border-2 border-emerald-300 focus:border-emerald-600 outline-none text-2xl font-black text-center"
                  data-testid="cashbox-input"/>
                {cashInput && !isNaN(parseFloat(String(cashInput).replace(/,/g, ""))) && (
                  <div className="text-center text-emerald-700 mt-1 font-bold">
                    {fmt(parseFloat(String(cashInput).replace(/,/g, "")))} د.ع
                  </div>
                )}
              </label>
              <button type="button" onClick={saveCashBox} disabled={savingCash}
                className="w-full py-3 rounded-lg bg-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-cashbox-btn">
                {savingCash ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                حفظ وتأكيد
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vouchers Modal */}
      {showVouchers && (
        <VouchersModal onClose={() => setShowVouchers(false)} onCreated={load} />
      )}

      {/* Ledger Modal */}
      {showLedger && (
        <CashBoxLedgerModal onClose={() => setShowLedger(false)} />
      )}
    </div>
  );
};

export default AdminAccountsTab;
